import type { ConsolaInstance } from "consola"
import type { Context } from "hono"

import type { AccountSelectionReason } from "~/lib/accounts-manager"
import type { AffinityKeySource } from "~/lib/utils"
import type { ChatCompletionsPayload } from "~/services/copilot/create-chat-completions"
import type { Model } from "~/services/copilot/get-models"

import { getAliasTargetSet } from "~/lib/config"
import { getRequestHistoryStore } from "~/lib/request-history"
import { getTokenCount } from "~/lib/tokenizer"

import type {
  AnthropicMessagesPayload,
  AnthropicResponse,
} from "./anthropic-types"
export {
  getCompactType,
  isCompactRequest,
  mergeToolResultForClaude,
  stripCacheControl,
} from "./preprocess"

export function mapOpenAIStopReasonToAnthropic(
  finishReason: "stop" | "length" | "tool_calls" | "content_filter" | null,
): AnthropicResponse["stop_reason"] {
  if (finishReason === null) {
    return null
  }
  const stopReasonMap = {
    stop: "end_turn",
    length: "max_tokens",
    tool_calls: "tool_use",
    content_filter: "end_turn",
  } as const
  return stopReasonMap[finishReason]
}

export const estimateInputTokens = async (
  payload: ChatCompletionsPayload,
  selectedModel: Model,
  logger: Pick<ConsolaInstance, "warn">,
): Promise<number | undefined> => {
  try {
    const tokenCount = await getTokenCount(payload, selectedModel)
    return tokenCount.input
  } catch (error) {
    logger.warn("Failed to estimate input tokens for message_start", error)
    return undefined
  }
}

type SelectionFailureReason = "MODEL_NOT_SUPPORTED" | "NO_QUOTA" | "NO_ACCOUNTS"

type SelectionFailure = {
  ok: false
  reason: SelectionFailureReason
  selectionReason?: AccountSelectionReason
}

type SelectionFailureContext = {
  c: Context
  store: ReturnType<typeof getRequestHistoryStore>
  requestId: string
  startedAtMs: number
  method: string
  path: string
  streamRequested: boolean
  clientModel: string
  clientIp?: string
  clientIpSource?: string
  userAgent?: string
  userId?: string
  safetyIdentifier?: string
  promptCacheKey?: string
  initiator?: "agent" | "user"
  isSubagent?: boolean
  affinityKeyUsed?: string
  affinityKeySource?: AffinityKeySource
  selectionReason?: AccountSelectionReason
  responsesItemOwnerLookupKeys?: ReadonlyArray<string>
  selection: SelectionFailure
}

function stringifyOwnerKeys(keys?: ReadonlyArray<string>): string | undefined {
  return keys && keys.length > 0 ? JSON.stringify(keys) : undefined
}

export const isWarmupProbeRequest = (
  payload: AnthropicMessagesPayload,
): boolean => {
  const lastMsg = payload.messages.at(-1)
  if (!lastMsg || lastMsg.role !== "user" || !Array.isArray(lastMsg.content)) {
    return false
  }

  const lastBlock = lastMsg.content.at(-1)
  if (!lastBlock || lastBlock.type !== "text") {
    return false
  }

  const text = lastBlock.text.trim().toLowerCase()
  const isEphemeral = lastBlock.cache_control?.type === "ephemeral"
  if (!isEphemeral) return false

  if (text === "warmup") return true

  if (text === "hello") {
    const preludeBlocks = lastMsg.content.slice(0, -1)
    if (preludeBlocks.length === 0) return false

    return preludeBlocks.every(
      (block) =>
        block.type === "text"
        && block.text.trimStart().toLowerCase().startsWith("<system-reminder"),
    )
  }

  return false
}

export const handleSelectionFailure = (
  context: SelectionFailureContext,
): Response => {
  const {
    c,
    store,
    requestId,
    startedAtMs,
    method,
    path,
    streamRequested,
    clientModel,
    clientIp,
    clientIpSource,
    userAgent,
    userId,
    safetyIdentifier,
    promptCacheKey,
    initiator,
    isSubagent,
    affinityKeyUsed,
    affinityKeySource,
    selectionReason,
    responsesItemOwnerLookupKeys,
    selection,
  } = context
  const finishedAtMs = Date.now()

  store.insert({
    requestId,
    startedAtMs,
    finishedAtMs,
    durationMs: finishedAtMs - startedAtMs,
    method,
    path,
    stream: streamRequested,
    clientModel,
    clientIp,
    clientIpSource,
    userAgent,
    userId,
    safetyIdentifier,
    promptCacheKey,
    initiator,
    isSubagent,
    affinityKeyUsed,
    affinityKeySource,
    responsesItemOwnerLookupKeysJson: stringifyOwnerKeys(
      responsesItemOwnerLookupKeys,
    ),
    httpStatus: selection.reason === "MODEL_NOT_SUPPORTED" ? 400 : 429,
    selectionReason: selectionReason ?? selection.selectionReason,
    selectionFailureReason: selection.reason,
  })

  if (selection.reason === "MODEL_NOT_SUPPORTED") {
    return c.json(
      {
        error: {
          message: `Model "${clientModel}" is not available for any configured account.`,
          type: "invalid_request_error",
        },
      },
      400,
    )
  }

  return c.json(
    {
      error: {
        message:
          "All accounts have exhausted their quota. Please wait for quota refresh or add additional accounts.",
        type: "rate_limit_error",
      },
    },
    429,
  )
}

export const maybeBlockOriginalModelName = (
  context: Omit<SelectionFailureContext, "selection">,
): Response | null => {
  const blockedTargets = getAliasTargetSet()
  if (!blockedTargets.has(context.clientModel.toLowerCase())) {
    return null
  }

  return handleSelectionFailure({
    ...context,
    selection: { ok: false, reason: "MODEL_NOT_SUPPORTED" },
  })
}
